// src/hooks/useCompany.ts
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { mockCompanyProfile } from "@/mocks/companyProfile";
import type { CompanyProfile } from "@/types/company";

export function useCompany(searchQuery?: string) {
    const [companies, setCompanies] = useState<CompanyProfile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                setLoading(true);
                // TODO: Implement actual API call
                // const response = await fetch(`/api/companies${searchQuery ? `?search=${searchQuery}` : ''}`);
                // const data = await response.json();

                // Use mock data for now
                let filteredCompanies = [...mockCompanyProfile];
                
                // Apply search filter if search query is provided
                if (searchQuery && searchQuery.length >= 3) {
                    const query = searchQuery.toLowerCase();
                    filteredCompanies = filteredCompanies.filter(
                        (company) =>
                            company.companyName.toLowerCase().includes(query) ||
                            company.psNumber.toLowerCase().includes(query) ||
                            company.glnNumber.toLowerCase().includes(query) ||
                            company.generalEmail.toLowerCase().includes(query)
                    );
                }
                
                setCompanies(filteredCompanies);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error(t("error.company.failedToLoadCompanies")));
            } finally {
                setLoading(false);
            }
        };

        loadCompanies();
    }, [searchQuery]);

    const addCompany = async (company: Omit<CompanyProfile, "id">): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // const response = await fetch("/api/companies", {
            //     method: "POST",
            //     headers: {
            //         "Content-Type": "application/json",
            //     },
            //     body: JSON.stringify(company),
            // });
            // const newCompany = await response.json();

            // Mock adding a company
            const newCompany = {
                ...company,
                id: String(companies.length + 1),
            } as CompanyProfile;
            setCompanies((prev) => [...prev, newCompany]);
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.company.failedToAddCompany"));
        }
    };

    const updateCompany = async (company: CompanyProfile): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // const response = await fetch(`/api/companies/${company.psNumber}`, {
            //     method: "PUT",
            //     headers: {
            //         "Content-Type": "application/json",
            //     },
            //     body: JSON.stringify(company),
            // });
            // const updatedCompany = await response.json();

            // Mock updating a company
            setCompanies((prev) =>
                prev.map((c) => (c.psNumber === company.psNumber ? company : c))
            );
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.company.failedToUpdateCompany"));
        }
    };

    const deleteCompany = async (id: string): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // await fetch(`/api/companies/${id}`, {
            //     method: "DELETE",
            // });

            // Mock deleting a company
            setCompanies((prev) => prev.filter((c) => c.psNumber !== id));
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.company.failedToDeleteCompany"));
        }
    };

    return {
        companies,
        loading,
        error,
        addCompany,
        updateCompany,
        deleteCompany,
    };
} 